<template>
  <div class="hazardous-declaration">
    <p class="intro-text">
      I declare that to the best of my knowledge, my luggage does <strong>NOT</strong> contain
      any of the following prohibited or dangerous items:
    </p>

    <div class="check-list">
      <label
        v-for="item in items"
        :key="item.key"
        class="check-item"
        :class="{ checked: checkedSet[item.key], none: item.key === 'none', priority: item.key === 'misc_dangerous' }"
      >
        <input
          type="checkbox"
          :checked="!!checkedSet[item.key]"
          @change="toggle(item.key)"
          class="check-input"
        />
        <span class="check-box">
          <svg v-if="checkedSet[item.key]" viewBox="0 0 16 16" class="check-icon">
            <path d="M3.5 8.5l3 3 6-7" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </span>
        <span class="check-text">
          <template v-if="item.key === 'misc_dangerous'">
            <strong>{{ item.label }}</strong>
            <span v-if="item.classLabel" class="check-class inline-class">{{ item.classLabel }}</span>
            <span class="misc-separator"> — </span>
            <template v-for="(example, idx) in item.examples">
              <strong :key="example" class="check-example-emphasis">{{ example }}</strong><span v-if="idx < item.examples.length - 1">, </span>
            </template>
          </template>
          <template v-else-if="item.key === 'none'">
            <strong>{{ item.label }}</strong>
          </template>
          <template v-else>
            <strong>{{ item.label }}</strong>
            <span class="check-class" v-if="item.classLabel">{{ item.classLabel }}</span>
          </template>
        </span>
      </label>
    </div>

    <transition name="warn-fade">
      <div v-if="hasDangerousChecked" class="warning-banner">
        <span class="warning-icon">&#9888;</span>
        Items containing prohibited / dangerous goods cannot be stored. Please remove these items from your luggage before proceeding.
      </div>
    </transition>

    <transition name="warn-fade">
      <div v-if="showSelectionHint" class="hint-banner">
        Please select <strong>None of above</strong> if your luggage does not contain any listed items.
      </div>
    </transition>
  </div>
</template>

<script>
const DECLARATION_ITEMS = [
  {
    key: 'misc_dangerous',
    label: 'Miscellaneous Dangerous Goods',
    classLabel: 'Class 9',
    examples: ['Power banks', 'Portable batteries', 'Petrol/diesel power tools']
  },
  { key: 'explosives', label: 'Explosives & Ammunition — Ammunition, Fireworks, Flares, Party poppers, Christmas crackers', classLabel: 'Class 1' },
  { key: 'aerosols_toxic', label: 'Aerosols & Toxic Substances — Hairspray, Fly spray, Deodorant spray, Butane canisters', classLabel: 'Class 2.1.2A & Class 6' },
  { key: 'flammable_liquids', label: 'Flammable Liquids & Acids — Petrol, Diesel, Solvents, Hand sanitiser, Nail polish remover, Sulphuric acid', classLabel: 'Class 3 & Class 8' },
  { key: 'toxic_chemicals', label: 'Toxic Chemicals — Pesticides, Treatment chemicals', classLabel: '' },
  { key: 'flammable_solids', label: 'Flammable Solids & Alkali — Matches, Firelighters, Sodium/Potassium hydroxide, Bleach, Cleaning products, Hair dye, Two-part adhesives/resins', classLabel: 'Class 4 & Class 8' },
  { key: 'oxidisers', label: 'Oxidisers — Fertiliser', classLabel: 'Class 5.1.1' },
  { key: 'none', label: 'None of above', classLabel: '' }
]

export default {
  name: 'HazardousDeclaration',
  data () {
    return {
      items: DECLARATION_ITEMS,
      checkedSet: {}
    }
  },
  computed: {
    checkedKeys () {
      return Object.keys(this.checkedSet).filter(k => this.checkedSet[k])
    },
    hasDangerousChecked () {
      return this.checkedKeys.some(k => k !== 'none')
    },
    hasNoneChecked () {
      return !!this.checkedSet.none
    },
    isValid () {
      return this.hasNoneChecked && !this.hasDangerousChecked
    },
    showSelectionHint () {
      return !this.hasNoneChecked && !this.hasDangerousChecked
    }
  },
  watch: {
    isValid: {
      immediate: true,
      handler (v) { this.$emit('valid', !!v) }
    },
    checkedKeys: {
      immediate: true,
      handler (v) { this.$emit('change', v.filter(k => k !== 'none')) }
    }
  },
  methods: {
    toggle (key) {
      if (key === 'none') {
        const next = !this.checkedSet.none
        if (next) {
          this.checkedSet = { none: true }
        } else {
          this.$set(this.checkedSet, 'none', false)
        }
        return
      }

      const next = !this.checkedSet[key]
      this.$set(this.checkedSet, key, next)
      if (next) {
        this.$set(this.checkedSet, 'none', false)
      }
    },
    getDeclaration () {
      return this.checkedKeys.filter(k => k !== 'none')
    },
    validate () {
      if (this.hasDangerousChecked) {
        return { valid: false, message: 'You cannot proceed while prohibited items are declared.' }
      }
      if (!this.hasNoneChecked) {
        return { valid: false, message: 'Please select None of above if your luggage does not contain any listed items.' }
      }
      return { valid: true, message: '' }
    }
  }
}
</script>

<style scoped>
.hazardous-declaration { margin-bottom: 20px; }
.intro-text { font-size: 14px; line-height: 1.55; margin-bottom: 16px; color: #374151; }
.check-list { display: flex; flex-direction: column; gap: 6px; }
.check-item { display: flex; align-items: flex-start; gap: 10px; padding: 10px 12px; background: #fff; border: 1px solid #e8ecf1; border-radius: 8px; cursor: pointer; transition: border-color .2s, background .2s; }
.check-item.priority { border-color: #fecaca; background: #fffbfb; }
.check-item:hover { border-color: #d1d5db; }
.check-item.checked { border-color: #ef4444; background: #fef2f2; }
.check-item.none.checked { border-color: #10b981; background: #ecfdf5; }
.check-input { position: absolute; opacity: 0; pointer-events: none; }
.check-box { width: 22px; height: 22px; border: 2px solid #d1d5db; border-radius: 4px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; margin-top: 1px; transition: border-color .2s, background .2s; }
.check-item.checked .check-box { border-color: #ef4444; background: #ef4444; }
.check-item.none.checked .check-box { border-color: #10b981; background: #10b981; }
.check-icon { width: 14px; height: 14px; color: #fff; }
.check-text { flex: 1; font-size: 14px; line-height: 1.4; color: #1a1a2e; }
.check-class { display: block; font-size: 12px; color: #6b7280; margin-top: 1px; }
.check-class.inline-class { display: inline; margin-top: 0; margin-left: 6px; }
.misc-separator { color: #374151; }
.check-example-emphasis { color: #ef4444; font-weight: 700; }
.warning-banner { margin-top: 14px; padding: 12px 14px; background: #fef2f2; border: 1px solid #fca5a5; border-radius: 8px; font-size: 13px; color: #991b1b; line-height: 1.5; }
.hint-banner { margin-top: 14px; padding: 12px 14px; background: #fffbeb; border: 1px solid #fcd34d; border-radius: 8px; font-size: 13px; color: #92400e; line-height: 1.5; }
.warning-icon { margin-right: 6px; }
.warn-fade-enter-active, .warn-fade-leave-active { transition: opacity .25s; }
.warn-fade-enter, .warn-fade-leave-to { opacity: 0; }
</style>



// WEBPACK FOOTER //
// src/components/HazardousDeclaration.vue